import React, { useMemo } from "react";
import PreIdVerification from "../../screens/auth/PreIdVerification";
import { MockedProvider } from "@apollo/client/testing/react";
import type { Meta, StoryObj } from "@storybook/react";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import { ProfileType, WorkingPermit, WorkingPermitType } from "../../__generated__/graphql";
import { currentLang } from "../../atoms/i18n";
import { MockedAuthManager } from "../../contexts/AuthManager";
import { buildUser, buildWorkerProfile } from "../factories";
import { MockedProfileSwitcherManager } from "../../contexts/ProfileSwitcherManager";
import { StoryStackNavigatorContainer } from "../display-helpers";
import { AtomsHydrator } from "../../atoms/atoms-hydrator";

const Stack = createNativeStackNavigator<{
    PreIdVerification: undefined;
}>();

type StoryComponentProps = {
    userPermit?: WorkingPermitType;
    profileType: ProfileType;
    nationality?: string;
};

const StoryComponent = ({ userPermit, profileType, nationality }: StoryComponentProps) => {
    const user = useMemo(() => {
        const user = buildUser({
            nationality: nationality,
            workerProfile: buildWorkerProfile({
                swissOrHasPermitAnswer: !!userPermit || nationality === "CH",
                lastWorkPermit: {
                    type: userPermit ?? null,
                    validFrom: null,
                    validTo: null,
                },
            }),
        });
        return user;
    }, [userPermit, nationality]);
    return (
        <AtomsHydrator atomValues={[[currentLang, "fr"]]}>
            <MockedAuthManager user={user}>
                <MockedProfileSwitcherManager profileType={profileType}>
                    <MockedProvider mocks={[]}>
                        <StoryStackNavigatorContainer>
                            <Stack.Navigator initialRouteName="PreIdVerification">
                                <Stack.Screen
                                    name="PreIdVerification"
                                    component={PreIdVerification}
                                    options={{ headerShown: false }}
                                />
                            </Stack.Navigator>
                        </StoryStackNavigatorContainer>
                    </MockedProvider>
                </MockedProfileSwitcherManager>
            </MockedAuthManager>
        </AtomsHydrator>
    );
};

const meta = {
    title: "Screens/PreIdVerification",
    component: StoryComponent,
    argTypes: {
        profileType: {
            options: [ProfileType.Client, ProfileType.Worker],
            control: {
                type: "radio",
            },
        },
        userPermit: WorkingPermit,
        nationality: String,
    },
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const Client: Story = {
    args: {
        profileType: ProfileType.Client,
        nationality: "CH",
    },
};

export const WorkerSwiss: Story = {
    args: {
        profileType: ProfileType.Worker,
        nationality: "CH",
    },
};

export const WorkerForeignWithPermit: Story = {
    args: {
        profileType: ProfileType.Worker,
        userPermit: WorkingPermitType.PermitB,
        nationality: "CH",
    },
};

export const WorkerForeign: Story = {
    args: {
        profileType: ProfileType.Worker,
        nationality: "FR",
    },
};
